import { Button, ButtonHandle } from "@progress/kendo-react-buttons";
import { Align, Popup } from "@progress/kendo-react-popup";
import classNames from "classnames";
import { EllipsisVertical as Icon, type LucideIcon } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { ISVG } from "@/icons/interface";
import useActionMenuKeyboard from "./useActionMenuKeyboard";
import "./style.scss";

// todo: improve any types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ActionProps<T = any> {
  title: string;
  onClick: (item: T) => void;
  disabled?: boolean | ((row: T) => boolean);
  hidden?: boolean | ((row: T) => boolean);
  ICon: React.FC<React.SVGProps<SVGSVGElement>> | React.FC<ISVG> | LucideIcon;
  className?: string;
  freemiumDisabled?: boolean;
  isEdit?: boolean;
  tooltipTitle?: string | ((row: T) => string | undefined);
}

export const ActionDropDown: React.FC<{
  actions: ActionProps[];
  className?: string;
}> = props => {
  const { t } = useTranslation();
  const actions = props.actions || [];

  const anchor = React.useRef<ButtonHandle | null>(null);
  const divRef = React.useRef<HTMLDivElement | null>(null);
  const [show, setShow] = React.useState(false);
  const { close, onOpen, onKeyDown } = useActionMenuKeyboard(
    divRef,
    anchor,
    setShow,
  );

  useOnClickOutside(divRef, () => setShow(false), {
    current: anchor.current?.element,
  });

  const anchorAlign: Align = {
    horizontal: "left",
    vertical: "top",
  };

  const popupAlign: Align = {
    horizontal: "right",
    vertical: "top",
  };

  return (
    <>
      <Button
        size="medium"
        onClick={event => {
          event.preventDefault();
          setShow(!show);
        }}
        ref={anchor}
        aria-haspopup="menu"
        aria-expanded={show}
        aria-label={t("common.moreActions")}
        fillMode="flat"
        className={`custom-action-dropDown-button ${props.className || ""}`}
      >
        <span className="k-icon tlx-svg-icon k-button-icon">
          <Icon />
        </span>
      </Button>
      <Popup
        anchor={anchor.current?.element}
        show={show}
        popupClass={"tlx-popup-action-container"}
        anchorAlign={anchorAlign}
        popupAlign={popupAlign}
        onOpen={onOpen}
      >
        <div
          ref={divRef}
          className="tlx-popup-action-items"
          role="menu"
          onKeyDown={onKeyDown}
        >
          {actions.map((item, i) => (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              aria-disabled={item.disabled ? true : undefined}
              className={classNames("tlx-popup-action-item", {
                "k-disabled": item.disabled,
              })}
              key={`${item.title}-${i}`}
              onClick={
                !item.disabled
                  ? () => {
                      close();
                      item.onClick(item);
                    }
                  : undefined
              }
            >
              <span className="icon-wrapper" aria-hidden="true">
                <item.ICon />
              </span>
              <span className={item.className || ""}>{item.title}</span>
            </button>
          ))}
        </div>
      </Popup>
    </>
  );
};
